import { DependencyList, useEffect, useState } from "react";

type Options = {
    /** Refetch this often while the page is open (ms). */
    refreshMs?: number;
    /** Retry this soon when the backend answers `pending` (its payload is still being built). */
    pendingRetryMs?: number;
    /** False: fetch nothing (the caller does not show this data in its current layout). */
    enabled?: boolean;
};

/**
 * Fetch-on-mount with periodic refresh, for the front page's panels.
 *
 * A response carrying `pending: true` means the backend is building that payload in
 * the background; the hook keeps the previous data and asks again shortly.
 */
export const usePulseData = <T>(fetcher: (signal: AbortSignal) => Promise<T>, deps: DependencyList = [], { refreshMs = 5 * 60_000, pendingRetryMs = 15_000, enabled = true }: Options = {}) => {
    const [data, setData] = useState<T | null>(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (!enabled) return;
        const controller = new AbortController();
        let timer: ReturnType<typeof setTimeout>;
        let alive = true;

        const load = async () => {
            let pending = false;
            try {
                const result = await fetcher(controller.signal);
                if (!alive) return;
                pending = !!(result && typeof result === "object" && (result as any).pending);
                if (!pending || data === null) setData(result);
                setFailed(false);
            } catch {
                if (!alive) return;
                setFailed(true);
            } finally {
                if (alive) setLoading(false);
            }
            if (alive) timer = setTimeout(load, pending ? pendingRetryMs : refreshMs);
        };
        setLoading(true);
        load();
        return () => {
            alive = false;
            controller.abort();
            clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...deps, enabled]);

    return { data, loading, failed };
};
